/**
 * Lightweight send gamification (guest-first, local-first). Without account or
 * backend: the accumulated counters and the unlocked achievements live in
 * `localStorage`. The logic of what has been earned is a PURE function
 * (`evaluateAchievements`) so it can be tested without storage; the
 * persistence layer is a thin, best-effort wrapper (if the storage fails, the
 * send does NOT break — the gamification is decorative).
 */

import { isRecord } from "@enviar/core";

const STORAGE_KEY = "enviar.achievements.v1";
const MB = 1024 * 1024;

export type AchievementId =
  | "first-send"
  | "five-sends"
  | "multi-file"
  | "hundred-mb"
  | "gigabyte-club";

interface AchievementDef {
  id: AchievementId;
  /** Threshold evaluated over the accumulated stats. */
  earned: (stats: SendStats) => boolean;
}

/** Accumulated stats of all the sends from this device. */
interface SendStats {
  sends: number;
  files: number;
  bytes: number;
}

interface AchievementState extends SendStats {
  unlocked: AchievementId[];
}

/** Achievement catalogue. Order = presentation order. */
export const ACHIEVEMENTS: readonly AchievementDef[] = [
  { id: "first-send", earned: (s) => s.sends >= 1 },
  { id: "five-sends", earned: (s) => s.sends >= 5 },
  { id: "multi-file", earned: (s) => s.files >= 10 },
  { id: "hundred-mb", earned: (s) => s.bytes >= 100 * MB },
  { id: "gigabyte-club", earned: (s) => s.bytes >= 1024 * MB },
];

const EMPTY: AchievementState = { sends: 0, files: 0, bytes: 0, unlocked: [] };

/**
 * PURE: given the accumulated state, returns the COMPLETE list of earned achievements
 * (the already unlocked ones plus the new ones). The caller compares with the previous
 * one to know what is new.
 */
export function evaluateAchievements(stats: SendStats): AchievementId[] {
  return ACHIEVEMENTS.filter((a) => a.earned(stats)).map((a) => a.id);
}

function isAchievementId(value: unknown): value is AchievementId {
  return ACHIEVEMENTS.some((a) => a.id === value);
}

export function loadAchievements(): AchievementState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { ...EMPTY };
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return { ...EMPTY };
    }
    return {
      sends: typeof parsed.sends === "number" ? parsed.sends : 0,
      files: typeof parsed.files === "number" ? parsed.files : 0,
      bytes: typeof parsed.bytes === "number" ? parsed.bytes : 0,
      unlocked: Array.isArray(parsed.unlocked) ? parsed.unlocked.filter(isAchievementId) : [],
    };
  } catch {
    return { ...EMPTY };
  }
}

/**
 * Records a completed send: adds to the accumulated counters, recalculates the achievements and
 * persists. Returns ONLY the newly unlocked achievements (to celebrate them).
 */
export function recordSendAchievements(delta: { files: number; bytes: number }): AchievementId[] {
  const prev = loadAchievements();
  const next: SendStats = {
    sends: prev.sends + 1,
    files: prev.files + Math.max(0, delta.files),
    bytes: prev.bytes + Math.max(0, delta.bytes),
  };
  const unlocked = evaluateAchievements(next);
  const fresh = unlocked.filter((id) => !prev.unlocked.includes(id));
  const state: AchievementState = { ...next, unlocked };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage full/blocked: the gamification is best-effort */
  }
  return fresh;
}

export function clearAchievements(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
